import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export interface SiteStats {
  todayVisitors: number;
  yesterdayVisitors: number;
  maxVisitors: number;
  totalVisitors: number;
}

interface UseSiteStatsReturn {
  stats: SiteStats;
  loading: boolean;
  error: string | null;
  refreshStats: () => Promise<void>;
}

function getTodayStr(): string {
  return new Date().toISOString().split("T")[0];
}

function getYesterdayStr(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split("T")[0];
}

export function useSiteStats(): UseSiteStatsReturn {
  const [stats, setStats] = useState<SiteStats>({
    todayVisitors: 0,
    yesterdayVisitors: 0,
    maxVisitors: 0,
    totalVisitors: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setError(null);
      const today = getTodayStr();
      const yesterday = getYesterdayStr();

      const { data: todayData, error: todayError } = await supabase
        .from("attendances")
        .select("user_id", { count: "exact", head: false })
        .eq("check_date", today);

      if (todayError) throw todayError;

      const { data: yesterdayData, error: yesterdayError } = await supabase
        .from("attendances")
        .select("user_id", { count: "exact", head: false })
        .eq("check_date", yesterday);

      if (yesterdayError) throw yesterdayError;

      const { data: allData, error: allError } = await supabase
        .from("attendances")
        .select("user_id, check_date");

      if (allError) throw allError;

      const uniqueUserIds = new Set<string>();
      const dayCountMap: Record<string, number> = {};

      (allData || []).forEach((row) => {
        uniqueUserIds.add(row.user_id);
        dayCountMap[row.check_date] = (dayCountMap[row.check_date] || 0) + 1;
      });

      let maxDay = 0;
      Object.values(dayCountMap).forEach((count) => {
        if (count > maxDay) maxDay = count;
      });

      setStats({
        todayVisitors: (todayData || []).length,
        yesterdayVisitors: (yesterdayData || []).length,
        maxVisitors: maxDay,
        totalVisitors: uniqueUserIds.size,
      });
    } catch {
      setError("통계를 불러오지 못했습니다");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const refreshStats = useCallback(async () => {
    setLoading(true);
    await fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refreshStats };
}